import { CircularProgress } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useSnackbar } from 'notistack';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useOnboardingDocuments,
  useSubmitOnboardingDocuments,
  useUploadOnboardingDocument
} from '../../../../queries/pxtraCard';
import { Button } from '../../atomic/button';
import { FeedbackAlert } from '../../atomic/feedback/alert';
import { DocumentDropZone } from './documentDropZone';
import { onboardingErrorMessage } from './errorMessage';

type Props = {
  companyId: string;
  /** What pxtra last asked Swan for; Swan's own status decides what is offered. */
  reviewRequested: boolean;
};

export const DocumentsBlock: FC<Props> = observer(({ companyId, reviewRequested }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const onError = (error: unknown) =>
    enqueueSnackbar(onboardingErrorMessage(error) ?? t('pxtraCard:onboardingActionFailed'), {
      variant: 'error'
    });
  const { data: collection, isPending, isError, refetch } = useOnboardingDocuments(companyId, true);
  const { mutate: upload, isPending: isUploading } = useUploadOnboardingDocument(companyId);
  const { mutate: submit, isPending: isSubmitting } = useSubmitOnboardingDocuments(companyId);

  // Without this the step says "upload here" with nothing to upload into,
  // so the manager is told what happened and given a way to try again.
  if (isError) {
    return (
      <div className="mt-3 max-w-[560px]">
        <FeedbackAlert
          severity="error"
          bulletPoints={false}
          description={t('pxtraCard:documentsUnavailable')}
          actionText={t('pxtraCard:documentsRetry')}
          actionCallback={() => void refetch()}
        />
      </div>
    );
  }

  // The read retries for a few seconds before it gives up; a blank step in
  // the meantime looks like there is nothing to upload.
  if (isPending || !collection) {
    return (
      <div className="mt-3 flex items-center gap-2 text-sm text-grey-600">
        <CircularProgress size={16} color="inherit" />
        {t('pxtraCard:documentsLoading')}
      </div>
    );
  }

  // Swan refuses uploads outside `waitingfordocument`, so the zones come down
  // rather than offering something it will reject.
  if (!collection.accepts_uploads) {
    return (
      <div className="mt-3 max-w-[560px]">
        <FeedbackAlert
          severity={collection.status === 'rejected' ? 'error' : 'info'}
          bulletPoints={false}
          description={t(`pxtraCard:documentsCollection_${collection.status || 'pendingreview'}`)}
        />
      </div>
    );
  }

  const complete = collection.documents.every((document) => document.uploaded);

  return (
    <div className="mt-2 max-w-[560px]">
      {collection.documents.map((document) => (
        <DocumentDropZone
          key={document.name}
          document={document}
          disabled={isUploading}
          onUpload={(documentType, file) =>
            upload({ purpose: document.name, documentType, file }, { onError })
          }
        />
      ))}

      <p className="m-0 mt-3 text-xs text-grey-600">{t('pxtraCard:documentsAccepted')}</p>
      <p className="m-0 mt-1 text-xs text-grey-600">{t('pxtraCard:documentsSubmitHint')}</p>
      <Button
        size="medium"
        className="mt-2"
        disabled={!complete || isSubmitting || reviewRequested}
        onClick={() => submit(undefined, { onError })}
      >
        {t('pxtraCard:documentsSubmit')}
      </Button>
    </div>
  );
});
